import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { translator } from 'mage-i18n';
import { ButtonSecondary, IconSettings, SpacerVertical } from '@klarna/bubble-ui';
import { analytics, defaultContainerPropTypes, HorizontalWrapper, Selector } from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

/* eslint-disable no-restricted-imports */
import adobeLogo from '../../../static/logo-adobe-commerce-vertical-red.svg';
import shopifyLogo from '../../../static/logo-shopify.svg';
import shoperLogo from '../../../static/logo-shoper-blue.svg';
import woocommerceLogo from '../../../static/logo-woocommerce.svg';
import bigcommerceLogo from '../../../static/logo-bigcommerce.svg';
import searchIcon from '../../../static/icon-search.svg';
import threeDCartLogo from '../../../static/logo-3dcart.svg';
import tiendanubeLogo from '../../../static/logo-tiendanube.png';
import lightspeedLogo from '../../../static/logo-lightspeed.svg';
import mollieLogoNew from '../../../static/logo-mollie-new.svg';
import plentymarketsLogo from '../../../static/logo-plentymarkets.svg';
import prestashopLogo from '../../../static/logo-prestashop.png';
import shopware5Logo from '../../../static/logo-shopware5.svg';
import shopware6Logo from '../../../static/logo-shopware6.svg';
import wixLogo from '../../../static/logo-wix.svg';
import wikinggruppenLogo from '../../../static/logo-wikinggruppen.svg';
import quickbutikLogo from '../../../static/logo-quickbutik.svg';
import abicartLogo from '../../../static/logo-abicart.svg';
import timmaLogo from '../../../static/logo-timma.png';
import magentoLogo from '../../../static/logo-magento.svg';
import dandomainLogo from '../../../static/logo-dandomain.png';
/* eslint-enable no-restricted-imports */

import { CAPS } from '@/constants';
import * as a from '@/actions/signup';
import { ContainerTemplate } from '@/components';
import { withCapabilityBeforeNext } from '@/flow/common';
import { getStandardPlatforms } from '@/selectors/platforms';
import { getSelectedCountryKey } from '@/selectors/data';

import { ANALYTICS_EVENTS } from './analyticsEvents';
import { CY_PLATFORM_CONTAINER_IDS, OTHER_VALUE, T_PREFIX } from './constants';

const logoProps = { backgroundColorToken: 'colors/backgrounds/transparent', hasBorder: false };

const PLATFORM_LOGO_URL_MAP = {
  shopify: shopifyLogo,
  shoper: shoperLogo,
  adobe: adobeLogo,
  woocommerce: woocommerceLogo,
  bigcommerce: bigcommerceLogo,
  '3dcart': threeDCartLogo,
  lightspeed: lightspeedLogo,
  molliede: mollieLogoNew,
  mollieat: mollieLogoNew,
  mollienl: mollieLogoNew,
  molliebe: mollieLogoNew,
  molliefr: mollieLogoNew,
  plentymarkets: plentymarketsLogo,
  prestashop: prestashopLogo,
  shopware: shopware5Logo,
  shopware_6: shopware6Logo,
  tiendanube: tiendanubeLogo,
  timmaLogo: timmaLogo,
  wix: wixLogo,
  wikinggruppen: wikinggruppenLogo,
  quickbutik: quickbutikLogo,
  abicart: abicartLogo,
  adobecommerce: magentoLogo,
  dandomain: dandomainLogo,
  [OTHER_VALUE]: searchIcon,
};

export const Platform = ({
  country,
  isDisabled,
  onBack,
  onChangePlatform,
  onNext,
  platforms,
  t,
}) => {
  const [loading, setLoading] = useState(false);

  const options = [
    ...platforms.map(({ id, name }) => ({
      value: id,
      label: id === 'custom' ? t(`${T_PREFIX}.custom`) : name,
      logoUri: id === 'custom' ? undefined : PLATFORM_LOGO_URL_MAP[id],
      icon: id === 'custom' ? IconSettings : undefined,
      logoProps,
      ...generateCyElemId(CY_PLATFORM_CONTAINER_IDS.PLATFORM(id), window),
    })),
    {
      value: OTHER_VALUE,
      label: t(`${T_PREFIX}.other_platform`),
      logoUri: PLATFORM_LOGO_URL_MAP[OTHER_VALUE],
      logoProps,
      ...generateCyElemId(CY_PLATFORM_CONTAINER_IDS.PLATFORM(OTHER_VALUE), window),
    },
  ];

  const onSelect = useCallback(
    platform => {
      if (loading) {
        return;
      }
      setLoading(true);
      analytics.logEvent(ANALYTICS_EVENTS.selectedOtherPlatformOption);

      onNext({
        payload: { params: { country, platform } },
        onNextCallback: () => onChangePlatform(platform),
      }).catch(() => setLoading(false));
    },
    [loading],
  );

  return (
    <ContainerTemplate
      title={t(`${T_PREFIX}.title_line_1`) + ' ' + t(`${T_PREFIX}.title_line_2`)}
      subtitle={t(`${T_PREFIX}.subtitle`)}
      showRecaptchaFooter
    >
      <Selector
        id="platform--selector"
        disabled={isDisabled}
        loading={loading}
        options={options}
        onSelect={onSelect}
      />
      <SpacerVertical spaceToken="space/600" />
      <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-start">
        <ButtonSecondary
          onClick={onBack}
          disabled={isDisabled || loading}
          {...generateCyElemId(CY_PLATFORM_CONTAINER_IDS.BTN_BACK, window)}
        >
          {t(`${T_PREFIX}.buttons.back`)}
        </ButtonSecondary>
      </HorizontalWrapper>
    </ContainerTemplate>
  );
};

Platform.propTypes = {
  ...defaultContainerPropTypes,
  platforms: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
  onChangePlatform: PropTypes.func.isRequired,
};

export default compose(
  connect(
    state => ({
      country: getSelectedCountryKey(state),
      platforms: getStandardPlatforms(state),
      t: translator(state),
    }),
    dispatch => ({
      onChangePlatform: platform => a.onChangeFormValue('platform', platform)(dispatch),
    }),
  ),
  withCapabilityBeforeNext({ cap: CAPS.PRODUCTS_GET }, ({ payload }) => {
    const { platform } = payload.params;
    analytics.logEvent(ANALYTICS_EVENTS.platformSelected, { platform });
  }),
)(Platform);
